/**
 * Shared transaction-dot tooltip — a clean dark card with a colour-accented
 * header and aligned label/value rows. Used as the `flyoutComponent` of a
 * Victory `VictoryTooltip` on both the candlestick and main profit charts so
 * the two charts share one tooltip design.
 */

export interface TooltipRow {
  label: string;
  value: string;
  /** Colours the value — green gain, red loss, neutral otherwise. */
  tone?: 'pos' | 'neg';
}

export interface TooltipModel {
  title: string;
  /** Category accent (core white / take-profit yellow / stop-loss blue). */
  accent: string;
  /** Small right-aligned chip in the header, e.g. "#142". */
  badge?: string;
  rows: TooltipRow[];
}

const FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export const TOOLTIP_WIDTH = 218;

/** Card height for a model — header block + one line per row. */
export const tooltipModelHeight = (model?: TooltipModel): number =>
  model ? 64 + Math.max(0, model.rows.length - 1) * 17 : 0;

interface FlyoutProps {
  /** Victory-injected — the data point coordinates. */
  x?: number;
  y?: number;
  /** Victory-injected — the (constrained) flyout centre. */
  center?: { x?: number; y?: number };
  width?: number;
  height?: number;
  datum?: { tooltip?: TooltipModel };
}

const ChartTooltip = (props: FlyoutProps) => {
  const model = props.datum?.tooltip;
  if (!model) {
    return null;
  }

  const w = props.width ?? TOOLTIP_WIDTH;
  const h = props.height ?? tooltipModelHeight(model);
  const cx = props.center?.x ?? props.x ?? 0;
  const cy = props.center?.y ?? props.y ?? 0;
  const left = cx - w / 2;
  const top = cy - h / 2;
  const pad = 13;

  return (
    <g>
      <rect
        x={left}
        y={top}
        width={w}
        height={h}
        rx={7}
        ry={7}
        fill="#0c0e12"
        stroke="rgba(255,255,255,0.13)"
        strokeWidth={1}
      />
      {/* category accent down the left edge */}
      <rect
        x={left}
        y={top + 6}
        width={3}
        height={h - 12}
        rx={1.5}
        fill={model.accent}
      />
      {/* header */}
      <circle cx={left + pad + 4} cy={top + 16} r={3.6} fill={model.accent} />
      <text
        x={left + pad + 14}
        y={top + 20}
        fontFamily={FONT}
        fontSize={11}
        fontWeight={700}
        fill={model.accent}
        letterSpacing="0.07em"
      >
        {model.title}
      </text>
      {model.badge && (
        <text
          x={left + w - pad}
          y={top + 20}
          fontFamily={FONT}
          fontSize={11}
          fontWeight={600}
          fill="#6f747c"
          textAnchor="end"
        >
          {model.badge}
        </text>
      )}
      <line
        x1={left + pad}
        x2={left + w - pad}
        y1={top + 31}
        y2={top + 31}
        stroke="rgba(255,255,255,0.09)"
        strokeWidth={1}
      />
      {/* label / value rows */}
      {model.rows.map((row, i) => {
        const baseline = top + 50 + i * 17;
        const valueColor =
          row.tone === 'neg'
            ? '#F0162F'
            : row.tone === 'pos'
            ? '#00B061'
            : '#eef0f2';
        return (
          <g key={`${row.label}_${i}`}>
            <text
              x={left + pad}
              y={baseline}
              fontFamily={FONT}
              fontSize={9.5}
              fill="#787d85"
              letterSpacing="0.03em"
            >
              {row.label}
            </text>
            <text
              x={left + w - pad}
              y={baseline}
              fontFamily={FONT}
              fontSize={10}
              fontWeight={600}
              fill={valueColor}
              textAnchor="end"
            >
              {row.value}
            </text>
          </g>
        );
      })}
    </g>
  );
};

export default ChartTooltip;
